import moment from 'moment-timezone';

interface WalkthroughScoreSummary {
	groupTitle: string;
	// Two-decimal strings from the backend ("75.00").
	percentageScore?: string | number | null;
	maxPossibleScore?: string | number | null;
}

// The fields the heatmap reads off a walkthrough returned by the heatmap search.
export interface HeatmapSourceWalkthrough {
	id: number;
	title?: string;
	name?: string;
	completedAt?: string | null;
	scores?: WalkthroughScoreSummary[];
}

export interface HeatmapWalkthrough extends HeatmapSourceWalkthrough {
	// Null when the walkthrough carries no usable score.
	percentageScore: number | null;
}

export interface HeatmapDay {
	percentageScore: number | null;
	walkthroughs: HeatmapWalkthrough[];
}

// Keyed by site-local date (YYYY-MM-DD).
export type HeatmapDataMap = Record<string, HeatmapDay>;

export type FetchHeatmapWalkthroughs = (
	siteSurveyId: string,
	startDate: moment.Moment,
	endDate: moment.Moment,
	targetCollectionName: string
) => Promise<HeatmapSourceWalkthrough[]>;

export interface HeatmapCell {
	date: string;
	dayOfMonth: number;
	// Average score percentage across the day's scored walkthroughs, or null when the day has no
	// walkthrough carrying a usable score. Kept numeric so the cell's color can be resolved against
	// the site survey's configured bands at render time.
	percentageScore: number | null;
	walkthroughs: HeatmapWalkthrough[];
	isToday: boolean;
	// Days after today have not happened yet, so they are drawn apart from genuine "No score" days.
	isFuture: boolean;
}

export interface HeatmapMonth {
	key: string;
	monthLabel: string;
	yearLabel: string;
	isCurrentMonth: boolean;
	// One column per calendar week the month touches, each holding 7 rows (index 0 = Sunday).
	// Rows falling outside the month are null so the month reads like a calendar page.
	weeks: (HeatmapCell | null)[][];
}

const DAYS_PER_WEEK = 7;

// The first day of the oldest month shown when `monthCount` whole months end with today's month.
export const getHeatmapRangeStart = (today: moment.Moment, monthCount: number) =>
	today
		.clone()
		.startOf('month')
		.subtract(monthCount - 1, 'months');

export const generateHeatmapMonths = (
	today: moment.Moment,
	monthCount: number,
	dataMap: HeatmapDataMap
): HeatmapMonth[] => {
	const todayKey = today.format('YYYY-MM-DD');
	const rangeStart = getHeatmapRangeStart(today, monthCount);

	return Array.from({ length: monthCount }, (unusedValue, monthIndex) => {
		const monthStart = rangeStart.clone().add(monthIndex, 'months');
		// day() is 0=Sunday in every locale, unlike startOf('week'), which follows the active
		// locale's first day of week — and other modules mutate that globally (EventCalendar sets
		// Monday-first on the shared 'en' locale). The row labels are hardcoded Sunday-first.
		const leadingBlankCount = monthStart.day();
		const daysInMonth = monthStart.daysInMonth();
		const weekCount = Math.ceil((leadingBlankCount + daysInMonth) / DAYS_PER_WEEK);

		const weeks: (HeatmapCell | null)[][] = Array.from({ length: weekCount }, () =>
			Array(DAYS_PER_WEEK).fill(null)
		);

		for (let dayOfMonth = 1; dayOfMonth <= daysInMonth; dayOfMonth++) {
			const slot = leadingBlankCount + dayOfMonth - 1;
			const date = monthStart.clone().date(dayOfMonth).format('YYYY-MM-DD');
			const cellData: HeatmapDay = dataMap[date] || { percentageScore: null, walkthroughs: [] };

			weeks[Math.floor(slot / DAYS_PER_WEEK)][slot % DAYS_PER_WEEK] = {
				date,
				dayOfMonth,
				percentageScore: cellData.percentageScore,
				walkthroughs: cellData.walkthroughs,
				isToday: date === todayKey,
				// YYYY-MM-DD strings sort chronologically, and comparing them avoids any time-zone
				// conversion between `today` and the bucketed dates.
				isFuture: date > todayKey,
			};
		}

		return {
			key: monthStart.format('YYYY-MM'),
			monthLabel: monthStart.format('MMMM'),
			yearLabel: monthStart.format('YYYY'),
			isCurrentMonth: monthIndex === monthCount - 1,
			weeks,
		};
	});
};
